/**
 * Direction 2 — "Court-side" app shell.
 *
 * Composition:
 *   <AppShell tab={...} onTab={...}>
 *     <SurfaceForCurrentTab />
 *   </AppShell>
 *
 * Renders the top bar (logo + Today/Groups/Chat tabs) and a floating
 * "Ask ClubBot" bubble (ChatDock, hidden on the Chat tab where that tab IS
 * the chat). On desktop the bubble opens a docked popup chat window (⌘K
 * toggles it); on mobile it routes to the Ask tab.
 */
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

import type { Me } from "../api";
import { ChatDock } from "./ChatDock";

export type Tab = "today" | "groups" | "chat";

export function AppShell({
  me,
  tab,
  onTab,
  onSignOut,
  onTextUpdates,
  onNotifications,
  onShowSetup,
  onShowAvailability,
  onExitImpersonation,
  children,
}: {
  me: Me;
  tab: Tab;
  onTab: (next: Tab) => void;
  onSignOut: () => void;
  onTextUpdates: () => void;
  onNotifications: () => void;
  onShowSetup: () => void;
  onShowAvailability: () => void;
  onExitImpersonation: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col">
      {me.impersonating && (
        <div className="flex items-center justify-center gap-3 bg-[#C84B31] px-4 py-2 text-bg text-center">
          <span className="font-mono text-[11px] uppercase tracking-wider">
            👤 Viewing as {me.name || me.email}
          </span>
          <button
            onClick={onExitImpersonation}
            className="cs-tap rounded-sm bg-bg/20 hover:bg-bg/30 px-3 py-1 font-display font-bold text-xs"
          >
            ↩ Return to admin
          </button>
        </div>
      )}
      <TopBar
        me={me}
        tab={tab}
        onTab={onTab}
        onSignOut={onSignOut}
        onTextUpdates={onTextUpdates}
        onNotifications={onNotifications}
        onShowSetup={onShowSetup}
        onShowAvailability={onShowAvailability}
      />
      {/* Chat owns its own full-height layout (composer pinned to bottom).
          Other tabs leave a little room so the bottom-right bubble doesn't
          cover the last bit of content. */}
      <main className={"flex-1 " + (tab === "chat" ? "" : "pb-24")}>
        {children}
      </main>
      {tab !== "chat" && (
        <ChatDock me={me} onSignOut={onSignOut} onMobileAsk={() => onTab("chat")} />
      )}
    </div>
  );
}

function TopBar({
  me,
  tab,
  onTab,
  onSignOut,
  onTextUpdates,
  onNotifications,
  onShowSetup,
  onShowAvailability,
}: {
  me: Me;
  tab: Tab;
  onTab: (t: Tab) => void;
  onSignOut: () => void;
  onTextUpdates: () => void;
  onNotifications: () => void;
  onShowSetup: () => void;
  onShowAvailability: () => void;
}) {
  return (
    <header className="border-b border-ink/10 bg-bg sticky top-0 z-30">
      <div className="max-w-5xl mx-auto px-3 sm:px-5 py-3 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="w-8 h-8 rounded-sm bg-ink flex items-center justify-center shrink-0">
            <div className="w-4 h-4 rounded-full bg-accent" />
          </div>
          {/* Wordmark hides below 400px so the tabs + avatar don't get squeezed. */}
          <div className="hidden xs:block font-display font-bold text-xl tracking-tight leading-none">
            ClubBot
          </div>
        </div>
        {/* Tighter on mobile: smaller padding + less letter-spacing so 3
            tabs + avatar comfortably fit at 375px. */}
        <nav className="flex items-center gap-0.5 sm:gap-1 font-mono text-[11px] uppercase tracking-[0.08em] sm:tracking-[0.14em]">
          <TabBtn active={tab === "today"} onClick={() => onTab("today")}>Home</TabBtn>
          <TabBtn active={tab === "groups"} onClick={() => onTab("groups")}>Clubs</TabBtn>
          <TabBtn active={tab === "chat"} onClick={() => onTab("chat")}>
            <span className="inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-accent" />
              <span className="sm:hidden">Ask</span>
              <span className="hidden sm:inline">Ask ClubBot</span>
            </span>
          </TabBtn>
        </nav>
        <ProfileMenu
          me={me}
          onSignOut={onSignOut}
          onTextUpdates={onTextUpdates}
          onNotifications={onNotifications}
          onShowSetup={onShowSetup}
          onShowAvailability={onShowAvailability}
        />
      </div>
    </header>
  );
}

function ProfileMenu({
  me,
  onSignOut,
  onTextUpdates,
  onNotifications,
  onShowSetup,
  onShowAvailability,
}: {
  me: Me;
  onSignOut: () => void;
  onTextUpdates: () => void;
  onNotifications: () => void;
  onShowSetup: () => void;
  onShowAvailability: () => void;
}) {
  const [open, setOpen] = useState(false);
  const initials = initialsFor(me);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-profile-menu]")) return;
      setOpen(false);
    }
    function esc(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    window.addEventListener("click", close);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="relative" data-profile-menu>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full bg-ink text-bg font-display font-bold text-sm flex items-center justify-center cs-tap hover:brightness-110"
        aria-label="Account menu"
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-11 min-w-[220px] bg-bg border-2 border-ink rounded-md shadow-lg z-40 overflow-hidden cs-modal-card">
          <div className="px-4 py-3 border-b border-ink/10">
            <div className="font-display font-bold text-sm truncate">
              {me.name || "Signed in"}
            </div>
            <div className="font-mono text-[10px] text-ink/60 truncate">
              {me.email}
            </div>
          </div>
          <button
            onClick={() => { setOpen(false); onShowSetup(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap border-b border-ink/10"
          >
            Getting started
          </button>
          <button
            onClick={() => { setOpen(false); onShowAvailability(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap border-b border-ink/10"
          >
            My availability
          </button>
          <button
            onClick={() => { setOpen(false); onNotifications(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap border-b border-ink/10"
          >
            Notifications
          </button>
          <button
            onClick={() => { setOpen(false); onTextUpdates(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap border-b border-ink/10"
          >
            Text notifications
          </button>
          <button
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function initialsFor(me: Me): string {
  const parts = (me.name ?? "").split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return me.email.slice(0, 2).toUpperCase();
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "px-2 sm:px-3 py-1.5 transition cs-tap " +
        (active
          ? "bg-ink text-bg"
          : "text-ink/70 hover:bg-ink/5")
      }
    >
      {children}
    </button>
  );
}

